import { useId } from "react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import type {
  Category,
  CreateCategoryRequest,
  CategoryAttributeRequest,
} from "@/services/api/categories";
import { useCategoryFormData } from "./hooks";
import { CategoryFormFields, AttributeSelector } from "./components";

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: Category;
  onSubmit: (data: CreateCategoryRequest) => void | Promise<void>;
  isLoading: boolean;
}

export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
  onSubmit,
  isLoading,
}: CategoryFormDialogProps) {
  const { formData, setFormData, availableAttributes } = useCategoryFormData({
    category,
    open,
  });
  const descriptionId = useId();

  const isSystem = category?.isSystem || false;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    const attributes: CategoryAttributeRequest[] =
      formData.selectedAttributes.map((attr) => ({
        attributeId: attr.attributeId,
        isMandatory: attr.isMandatory,
        order: attr.order,
      }));

    const displayName = formData.displayName.trim() || null;

    onSubmit({
      // System categories: keep original name; non-system: use form value
      name: isSystem ? category!.name : formData.name.trim(),
      displayName,
      icon: formData.icon || null,
      attributes,
    });
  };

  const addAttribute = (attributeId: number) => {
    setFormData((prev) => {
      if (
        prev.selectedAttributes.some((attr) => attr.attributeId === attributeId)
      )
        return prev;
      const nextOrder =
        prev.selectedAttributes.length > 0
          ? Math.max(...prev.selectedAttributes.map((a) => a.order)) + 1
          : 2;
      return {
        selectedAttributes: [
          ...prev.selectedAttributes,
          { attributeId, isMandatory: false, order: nextOrder },
        ],
      };
    });
  };

  const removeAttribute = (attributeId: number) => {
    setFormData((prev) => {
      const remaining = prev.selectedAttributes.filter(
        (attr) => attr.attributeId !== attributeId
      );
      return {
        selectedAttributes: remaining.map((attr, index) => ({
          ...attr,
          order: index + 2,
        })),
      };
    });
  };

  const updateAttribute = (attributeId: number, isMandatory: boolean) => {
    setFormData((prev) => ({
      selectedAttributes: prev.selectedAttributes.map((attr) =>
        attr.attributeId === attributeId ? { ...attr, isMandatory } : attr
      ),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden"
        aria-describedby={descriptionId}
      >
        <DialogHeader>
          <DialogTitle>
            {category ? "Edit Category" : "Add New Category"}
          </DialogTitle>
          <DialogDescription>
            {category
              ? "Update the category information below."
              : "Create a new category to organize your attributes."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 overflow-y-auto min-h-0">
            <CategoryFormFields
              formData={formData}
              onFieldChange={setFormData}
              isLoading={isLoading}
              isSystem={isSystem}
            />
            <div className="space-y-2 py-4">
              <label className="text-sm font-medium text-ink-900">
                Attributes
              </label>
              <AttributeSelector
                availableAttributes={availableAttributes}
                selectedAttributes={formData.selectedAttributes}
                onAdd={addAttribute}
                onRemove={removeAttribute}
                onUpdate={updateAttribute}
                isLoading={isLoading}
              />
            </div>
          </div>
          <DialogFooter className="pt-4 border-t border-grid-200 shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !formData.name.trim()}>
              {isLoading
                ? category
                  ? "Updating..."
                  : "Creating..."
                : category
                  ? "Update"
                  : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
